import React from 'react'
import {
  MetricCard,
  MetricCardGrid,
  metricCardFromReportsKpi,
} from '@enamelai/design-system'
import { ReportsKpiMetric } from '@dataTypes/reports.type'
import {
  toKpiValueDisplay,
  toSentenceCase,
  toSentenceCaseProse,
} from '@utils/textCase.util'
import '@pages/Reports/ReportsSkeleton.css'

type ReportKpiGridProps = {
  items: ReportsKpiMetric[]
  loading?: boolean
}

function isKpiLoadingValue(value: string): boolean {
  const trimmed = value.trim()
  return trimmed === '…' || trimmed === '...'
}

function getKpiGridColumns(count: number): 2 | 3 | 4 | 5 | 6 | 7 {
  if (count > 6) return 7
  return Math.max(2, count) as 2 | 3 | 4 | 5 | 6 | 7
}

function ReportKpiGrid({
  items,
  loading = false,
}: ReportKpiGridProps): React.ReactElement {
  const showSkeleton =
    loading ||
    (items.length > 0 && items.every((item) => isKpiLoadingValue(item.value)))
  const columns = getKpiGridColumns(items.length)

  if (showSkeleton) {
    return (
      <div
        className="reports-bundle-skeleton"
        aria-busy="true"
        aria-label="Loading metrics"
      >
        <MetricCardGrid
          columns={columns}
          className="reports-patient-base-health__kpi-grid"
        >
          {items.map((item) => (
            <div
              key={item.id}
              className="reports-bundle-skeleton-kpi reports-bundle-skeleton-shimmer"
            >
              <span className="reports-bundle-skeleton-line reports-bundle-skeleton-line--title" />
              <span className="reports-bundle-skeleton-line reports-bundle-skeleton-line--value" />
              <span className="reports-bundle-skeleton-line" />
            </div>
          ))}
        </MetricCardGrid>
      </div>
    )
  }

  return (
    <MetricCardGrid
      columns={columns}
      className="reports-patient-base-health__kpi-grid"
    >
      {items.map((item) => {
        const metricCard = metricCardFromReportsKpi(item)
        return (
          <MetricCard
            key={item.id}
            {...metricCard}
            label={toSentenceCase(metricCard.label ?? '')}
            value={
              typeof metricCard.value === 'string'
                ? toKpiValueDisplay(metricCard.value)
                : metricCard.value
            }
            footer={
              typeof metricCard.footer === 'string'
                ? toSentenceCaseProse(metricCard.footer)
                : metricCard.footer
            }
          />
        )
      })}
    </MetricCardGrid>
  )
}

export default ReportKpiGrid
